import React from 'react';
import './MobileNumberTextField.scss';
import {
   ViewState,
   MobileNumberDetails,
   MobileNumberValidationDetails,
   MobileNumberVerifyDetails,
} from './types';
import MobileNumberView from './MobileNumberView';
import MobileNumberEdit from './MobileNumberEdit';
import MobileNumberVerify from './MobileNumberVerify';
import MobileNumberLocked from './MobileNumberLocked';

export * from './types';

export interface MobileNumberTextFieldProps {
   mobileNumberDetails: MobileNumberDetails;
   initialMobileNumber: string;
   validationDetails?: MobileNumberValidationDetails;
   isValidatingMobileNumber?: boolean;
   sendOtpData?: MobileNumberValidationDetails;
   isSendingOtp?: boolean;
   resendOtpData?: MobileNumberValidationDetails;
   isResendingOtp?: boolean;
   isCancelling?: boolean;
   verifyDetails?: MobileNumberVerifyDetails;
   isVerifyingAndSaving?: boolean;
   resendCooldown?: number;
   lockoutSeconds?: number;
   viewState: ViewState;
   verificationCode?: string;
   isFetchingCurrentVerification?: boolean;
   isFetchingContactMobileNumber?: boolean;
   // Set by the host form from its own validity map, which stays empty until the first
   // save attempt - so only an explicit false marks the field, never a missing entry.
   inputValid?: boolean;
   onEdit?: () => void;
   onCancel?: () => void;
   onMobileNumberChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
   onSave?: () => void;
   onVerificationCodeChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
   onVerify?: () => void;
   onResend?: () => void;
}

const MobileNumberTextField = ({
   mobileNumberDetails,
   initialMobileNumber,
   validationDetails,
   isValidatingMobileNumber = false,
   sendOtpData,
   isSendingOtp = false,
   resendOtpData,
   isResendingOtp = false,
   isCancelling = false,
   verifyDetails,
   isVerifyingAndSaving = false,
   resendCooldown = 0,
   lockoutSeconds = 0,
   viewState,
   verificationCode = '',
   isFetchingCurrentVerification = false,
   isFetchingContactMobileNumber = false,
   inputValid,
   onEdit = () => {},
   onCancel = () => {},
   onMobileNumberChange = () => {},
   onSave = () => {},
   onVerificationCodeChange = () => {},
   onVerify = () => {},
   onResend = () => {},
}: MobileNumberTextFieldProps) => {
   switch (viewState) {
      case ViewState.EDIT:
         return (
            <MobileNumberEdit
               mobileNumberDetails={mobileNumberDetails}
               initialMobileNumber={initialMobileNumber}
               validationDetails={validationDetails}
               isValidatingMobileNumber={isValidatingMobileNumber}
               sendOtpData={sendOtpData}
               isSendingOtp={isSendingOtp}
               inputValid={inputValid}
               onMobileNumberChange={onMobileNumberChange}
               onSave={onSave}
               onCancel={onCancel}
            />
         );
      case ViewState.VALIDATE:
         return (
            <MobileNumberVerify
               mobileNumberDetails={mobileNumberDetails}
               verifyDetails={verifyDetails}
               resendOtpData={resendOtpData}
               resendCooldown={resendCooldown}
               isVerifyingAndSaving={isVerifyingAndSaving}
               isResendingOtp={isResendingOtp}
               isCancelling={isCancelling}
               verificationCode={verificationCode}
               inputValid={inputValid}
               onVerificationCodeChange={onVerificationCodeChange}
               onVerify={onVerify}
               onResend={onResend}
               onCancel={onCancel}
            />
         );
      case ViewState.LOCKED:
         return (
            <MobileNumberLocked
               mobileNumberDetails={mobileNumberDetails}
               lockoutSeconds={lockoutSeconds}
            />
         );
      case ViewState.SUCCESS:
         // Deliberately not marked from inputValid: the host's validity map is only
         // recomputed on save, so it can still hold the stale false that a just-completed
         // verification has already resolved - and an error sitting beside the success
         // message would contradict it.
         return (
            <MobileNumberView
               mobileNumberDetails={mobileNumberDetails}
               isFetchingCurrentVerification={isFetchingCurrentVerification}
               isFetchingContactMobileNumber={isFetchingContactMobileNumber}
               successMessage="Your mobile number has been verified."
               onEdit={onEdit}
            />
         );
      case ViewState.INITIAL:
      default:
         return (
            <MobileNumberView
               mobileNumberDetails={mobileNumberDetails}
               validationDetails={validationDetails}
               isFetchingCurrentVerification={isFetchingCurrentVerification}
               isFetchingContactMobileNumber={isFetchingContactMobileNumber}
               inputValid={inputValid}
               onEdit={onEdit}
            />
         );
   }
};

export default MobileNumberTextField;
